import type { ProductDetail } from '../../model/types';
import { UTCDate } from '@date-fns/utc';
import {
  CircleCheck,
  Copy,
  FileKey2,
  ImageOff,
  MapPin,
  Star,
} from 'lucide-react';
import { htmlToPlainText, sanitizeHtml } from '@/shared/lib';
import { Skeleton, Text } from '@/shared/ui';
import { uploadthingFileUrl } from '@/shared/uploadthing';
import { useAdultTravellerTypeIds } from '../../api/use-adult-traveller-type-ids';
import { extractDisplayPrice, formatPrice } from '../../lib/price';
import { resolveThumbnailUrl } from '../../lib/thumbnail-url';
import css from './product-detail-view.module.scss';

// Coerce a raw `attachments` jsonb entry into the minimal shape this view
// renders (url + caption). Lenient — legacy rows may carry only a `url` or a
// `fileKey`; mirrors the `toDto` helper in product-row-actions' upload modal.
type AttachmentImage = { id: string; url: string; caption: string | null };

const resolveAttachmentUrl = (raw: Record<string, unknown>): string => {
  if (typeof raw.url === 'string' && raw.url) return raw.url;
  if (typeof raw.fileKey === 'string' && raw.fileKey) {
    return uploadthingFileUrl(raw.fileKey);
  }
  return '';
};

// Image attachments only — legacy `PreviewModal` filters on
// `attachment.type === 'IMAGE'`, but tolerates rows with no `type` (older
// products predate the field) by treating them as images too.
const resolveImageAttachments = (value: unknown): AttachmentImage[] => {
  if (!Array.isArray(value)) return [];
  const out: AttachmentImage[] = [];
  value.forEach((raw, index) => {
    if (!raw || typeof raw !== 'object') return;
    const r = raw as Record<string, unknown>;
    const type = typeof r.type === 'string' ? r.type.toUpperCase() : null;
    if (type && type !== 'IMAGE') return;
    const url = resolveAttachmentUrl(r);
    if (!url) return;
    out.push({
      id: typeof r.id === 'string' ? r.id : url || `attachment-${index}`,
      url,
      caption: typeof r.caption === 'string' ? r.caption : null,
    });
  });
  return out;
};

const isLocalizedTextEntry = (
  v: unknown
): v is { languageCode: string; text: string } =>
  typeof v === 'object' &&
  v !== null &&
  'languageCode' in v &&
  'text' in v &&
  typeof (v as { text: unknown }).text === 'string';

const resolveLocalized = (value: unknown): string => {
  if (!Array.isArray(value)) return '';
  const en = value.find(
    (t): t is { languageCode: string; text: string } =>
      isLocalizedTextEntry(t) && t.languageCode === 'en'
  );
  if (en?.text) return en.text;
  const first = value.find(isLocalizedTextEntry);
  return first?.text ?? '';
};

const resolveSupplierLabel = (value: unknown): string => {
  if (!Array.isArray(value) || value.length === 0) return '—';
  const first = value[0] as Record<string, unknown> | null;
  const name =
    first && typeof first === 'object'
      ? typeof first.name === 'string'
        ? first.name
        : typeof first.supplierName === 'string'
          ? first.supplierName
          : null
      : null;
  if (!name) return '—';
  if (value.length === 1) return name;
  return `${name} +${value.length - 1} more`;
};

const formatLocation = (
  city: string | null,
  country: string | null
): string => {
  if (city && country) return `${city}, ${country}`;
  return city ?? country ?? '—';
};

const formatModifiedOn = (value: string | null): string => {
  if (!value) return '—';
  const d = new UTCDate(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

// Budget tier (1..4) → `$`..`$$$$`. Reads the manual override first, then the
// computed default; `—` when neither is populated (pricing track hasn't run).
const formatBudget = (
  budgetValue: number | null | undefined,
  defaultBudgetValue: number | null | undefined
): string => {
  const tier = budgetValue ?? defaultBudgetValue ?? null;
  if (tier == null || tier < 1) return '—';
  return '$'.repeat(tier);
};

type Props = {
  data: ProductDetail | null | undefined;
  isLoading: boolean;
  isError: boolean;
};

// Presentational product-detail body (image + fields + description).
// Shared by the standalone detail page and the planner's detail modal —
// no AppLayout, no header chrome, no navigation. Owns only rendering.
export const ProductDetailView = ({ data, isLoading, isError }: Props) => {
  const adultTravellerIds = useAdultTravellerTypeIds();
  if (isError) {
    return (
      <div className={css.error}>
        <Text variant="error">
          Failed to load product. It may have been deleted.
        </Text>
      </div>
    );
  }

  const descriptionHtml = sanitizeHtml(resolveLocalized(data?.description));
  // Public (customer-facing) notes — plain rich-text HTML string, same as the
  // "Public notes" edit tab writes. Distinct from `privateNotes`. Surfaced here
  // so the planner's product overview matches the customer preview.
  const notesHtml = sanitizeHtml(data?.notes ?? '');
  const thumbnailUrl = resolveThumbnailUrl(data?.thumbnail ?? null);
  const title = resolveLocalized(data?.title) || (data?.titleEn ?? 'Product');
  const displayPrice = extractDisplayPrice(data?.price, adultTravellerIds);
  const priceText = formatPrice(displayPrice, data?.currency ?? null);
  const supplierLabel = resolveSupplierLabel(data?.suppliers);
  // Internal product info — surfaced here so consultants can inspect it from
  // within the planner's product-detail modal (not only from the products
  // list). `byId` already returns both fields; legacy parity with the list's
  // private-notes button + the preview modal's image gallery.
  const privateNotes = data?.privateNotes?.trim() ? data.privateNotes : null;
  const images = resolveImageAttachments(data?.attachments);
  // Facilities (legacy PreviewModal parity). `byId` already resolves them to
  // `{ id, title }[]` (title is localized-text jsonb) but the view never showed
  // them. Resolve the EN title and drop empties.
  const facilities = (data?.facilities ?? [])
    .map((facility) => ({
      id: facility.id,
      title: resolveLocalized(facility.title),
    }))
    .filter((facility) => facility.title);

  const onCopyNotes = () => {
    // Private notes are rich-text (tiptap) HTML — copy as readable plain text
    // so the paste target doesn't get raw `<p>`/`<span>` markup.
    const text = htmlToPlainText(privateNotes);
    if (text) navigator.clipboard?.writeText(text);
  };

  return (
    <div className={css.root}>
      <div className={css.top}>
        <div className={css.image}>
          {thumbnailUrl ? (
            <img alt={title} className={css.imageImg} src={thumbnailUrl} />
          ) : (
            <ImageOff className={css.imagePlaceholder} size={48} />
          )}
        </div>

        <div className={css.fields}>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Category
            </Text>
            <Text>
              {isLoading ? (
                <Skeleton width="80px" />
              ) : (
                (data?.contentCategory ?? '—')
              )}
            </Text>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Subcategory
            </Text>
            <Text>
              {isLoading ? (
                <Skeleton width="80px" />
              ) : (
                (data?.contentSubcategory ?? '—')
              )}
            </Text>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Location
            </Text>
            <div className={css.fieldValue}>
              {(data?.city || data?.country) && (
                <MapPin size={14} style={{ color: 'var(--color-muted)' }} />
              )}
              <Text>
                {isLoading ? (
                  <Skeleton width="120px" />
                ) : (
                  formatLocation(data?.city ?? null, data?.country ?? null)
                )}
              </Text>
            </div>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Stars
            </Text>
            <div className={css.fieldValue}>
              {data?.stars && (
                <Star size={14} style={{ color: 'var(--color-warning)' }} />
              )}
              <Text>
                {isLoading ? <Skeleton width="40px" /> : (data?.stars ?? '—')}
              </Text>
            </div>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Price
            </Text>
            <Text weight="medium">
              {isLoading ? <Skeleton width="60px" /> : priceText}
            </Text>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Budget
            </Text>
            <Text>
              {isLoading ? (
                <Skeleton width="40px" />
              ) : (
                formatBudget(data?.budgetValue, data?.defaultBudgetValue)
              )}
            </Text>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Catalog
            </Text>
            <Text>
              {isLoading ? (
                <Skeleton width="100px" />
              ) : (
                (data?.catalogTitle ?? '—')
              )}
            </Text>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Supplier
            </Text>
            <Text>
              {isLoading ? <Skeleton width="120px" /> : supplierLabel}
            </Text>
          </div>
          <div className={css.field}>
            <Text size="xs" variant="muted" weight="medium">
              Last modified
            </Text>
            <Text>
              {isLoading ? (
                <Skeleton width="160px" />
              ) : (
                formatModifiedOn(data?.modifiedOn ?? null)
              )}
            </Text>
          </div>
        </div>
      </div>

      <div className={css.description}>
        <Text size="xs" variant="muted" weight="medium">
          Description
        </Text>
        {isLoading ? (
          <Skeleton width="100%" />
        ) : descriptionHtml ? (
          <div
            className={css.descriptionHtml}
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        ) : (
          <Text variant="muted">—</Text>
        )}
      </div>

      {!isLoading && facilities.length > 0 && (
        <div className={css.facilities}>
          <Text size="xs" variant="muted" weight="medium">
            Facilities
          </Text>
          <div className={css.facilitiesGrid}>
            {facilities.map((facility) => (
              <span className={css.facilityItem} key={facility.id}>
                <CircleCheck className={css.facilityIcon} size={15} />
                <Text>{facility.title}</Text>
              </span>
            ))}
          </div>
        </div>
      )}

      {!isLoading && notesHtml && (
        <div className={css.description}>
          <Text size="xs" variant="muted" weight="medium">
            Public notes
          </Text>
          <div
            className={css.descriptionHtml}
            dangerouslySetInnerHTML={{ __html: notesHtml }}
          />
        </div>
      )}

      {!isLoading && privateNotes && (
        <div className={css.privateNotes}>
          <div className={css.privateNotesHead}>
            <FileKey2 size={14} style={{ color: 'var(--color-muted)' }} />
            <Text size="xs" variant="muted" weight="medium">
              Private notes
            </Text>
            <button
              aria-label="Copy private notes"
              className={css.copyButton}
              onClick={onCopyNotes}
              title="Copy private notes"
              type="button"
            >
              <Copy size={13} />
            </button>
          </div>
          {/* Private notes are rich-text (tiptap) HTML — render sanitized
              markup like the description / public notes above, not the raw
              string (else literal `<p>`/`<span>` tags show as text). */}
          <div
            className={css.privateNotesBody}
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(privateNotes) }}
          />
        </div>
      )}

      {!isLoading && images.length > 0 && (
        <div className={css.attachments}>
          <Text size="xs" variant="muted" weight="medium">
            Attachments
          </Text>
          <div className={css.attachmentsGrid}>
            {images.map((img) => (
              <a
                className={css.attachment}
                href={img.url}
                key={img.id}
                rel="noreferrer"
                target="_blank"
                title={img.caption ?? undefined}
              >
                <img
                  alt={img.caption ?? ''}
                  className={css.attachmentImg}
                  loading="lazy"
                  src={img.url}
                />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
